import { useLocation } from 'react-router-dom';

import { useTranslation } from '@fluxora/i18n';
import type { StepState } from '@fluxora/ui';

import {
  currentFunnelStep,
  deriveFunnelSteps,
  type FunnelStepId,
} from '../services/funnelSteps';

/**
 * The enrollment progress rail — Plan → Approval → Estimate → Sign.
 *
 * Bespoke rather than the common `Stepper` since the premium pass: this one is
 * the storefront's own composition — a gradient progress fill that SLIDES to
 * the current step (scaleX, compositor-only), completed nodes wearing the
 * pastel brand tint with a check, and a ping halo on the current node (an
 * `::after` that scales and fades — see `.step-node-active` in globals.css).
 * All motion sits behind `prefers-reduced-motion: no-preference`; the STATES
 * never depend on it.
 *
 * ── A11Y ──────────────────────────────────────────────────────────────────
 * A real `<nav><ol>` — each step announces its label plus a state suffix
 * (" — complete" / " — current step" / " — upcoming") via sr-only text, and
 * the current one additionally carries `aria-current="step"`. The rail and
 * fill are decorative (`aria-hidden`).
 */
export function FunnelStepper(): JSX.Element | null {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  if (currentFunnelStep(pathname) === undefined) return null;

  const labels: Record<FunnelStepId, string> = {
    plan: t('shop.funnel.step.plan'),
    credit: t('shop.funnel.step.credit'),
    estimate: t('shop.funnel.step.estimate'),
    sign: t('shop.funnel.step.sign'),
  };
  // `StepState` also admits 'failed' (other Stepper consumers use it); the
  // funnel never derives it, so it reads as an upcoming step if it ever appears.
  const stateLabels: Record<StepState, string> = {
    done: t('shop.funnel.state.done'),
    active: t('shop.funnel.state.active'),
    pending: t('shop.funnel.state.pending'),
    failed: t('shop.funnel.state.pending'),
  };

  const steps = deriveFunnelSteps(pathname);
  const activeIdx = steps.findIndex((s) => s.state === 'active');
  // How far the fill reaches: to the ACTIVE node's center. Nothing active
  // (defensive — currentFunnelStep already guarded) leaves it empty.
  const fraction = activeIdx <= 0 ? 0 : activeIdx / (steps.length - 1);

  return (
    <nav aria-label={t('shop.funnel.aria')} data-testid="funnel-stepper" className="mb-1">
      <div className="relative">
        {/* The rail spans node-center to node-center; the gradient fill slides
            along it as the shopper advances. Both decorative. */}
        <div
          aria-hidden="true"
          className="absolute inset-x-5 top-[18px] h-[3px] overflow-hidden rounded-full bg-border/70"
        >
          <div
            className="stepper-fill h-full w-full rounded-full bg-[image:var(--brand-gradient)]"
            style={{ transform: `scaleX(${fraction})` }}
          />
        </div>

        <ol className="relative flex items-start justify-between">
          {steps.map((step, idx) => {
            const label = labels[step.id];
            const isDone = step.state === 'done';
            const isActive = step.state === 'active';
            return (
              <li
                key={step.id}
                className="flex flex-col items-center gap-1.5"
                {...(isActive ? { 'aria-current': 'step' as const } : {})}
              >
                <span
                  className={
                    isDone
                      ? 'flex h-10 w-10 items-center justify-center rounded-full bg-[image:var(--brand-gradient-tint)] text-foreground shadow-sm'
                      : isActive
                        ? 'step-node-active relative flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground shadow-pill'
                        : 'flex h-10 w-10 items-center justify-center rounded-full border-2 border-border bg-surface text-sm font-semibold text-foreground/40'
                  }
                >
                  {isDone ? (
                    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="h-4 w-4">
                      <path
                        d="M3 8.5 6.5 12 13 4.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    idx + 1
                  )}
                </span>
                <span
                  className={`text-xs font-semibold ${
                    isActive
                      ? 'text-foreground'
                      : isDone
                        ? 'text-foreground/70'
                        : 'text-foreground/40'
                  }`}
                >
                  {label}
                  <span className="sr-only">{stateLabels[step.state]}</span>
                </span>
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
}
